'use client'

import { useEffect } from 'react'

const abortAndClearItemFromQueue = <TEventDetail>(id: string, queue: EventDispatchQueueBuffer<TEventDetail>) => {
    queue[id]?.controller.abort()
    /* eslint-disable-next-line --  Assignment to property of function parameter
     * I am deliberately trying to mutate the reference to this object
     */
    delete queue[id]
}

const abortAllEventDispatchesAndClearQueue = <TEventDetail>(queue: EventDispatchQueueBuffer<TEventDetail>) => {
    Object.keys(queue).forEach((id: string) => {
        abortAndClearItemFromQueue(id, queue)
    })
}

interface EventDispatchQueueBufferEntry<TEventDetail> {
    event: CustomEvent<TEventDetail>
    controller: AbortController
}
interface EventDispatchQueueBuffer<TEventDetail> {
    [id: string]: EventDispatchQueueBufferEntry<TEventDetail>
}

export interface EventDispatchQueue<TEventDetail> {
    queueEvent: (
        id: string,
        event: CustomEvent<TEventDetail>,
        target: HTMLElement | Document,
        delayInMilliseconds: number,
    ) => void
    getQueuedEvent: (id: string) => EventDispatchQueueBufferEntry<TEventDetail> | undefined
    abortAndClearAll: () => void
}

/**
 * @description
 * Can be used to dispatch CustomEvents<TEventDatil>, especially when dispatching of events is done from within an
 * event listener that is triggered often.
 * the event is held in a queue for a specified amount of milliseconds. When a new event is queued with the same id,
 * dispatching of the previous event is aborted and the previous event is overwritten by the new event.
 *
 * events that are queued for dispatching can be retrieved. That is useful for when you want parts of the previous
 * CustomEvent.detail to be merged into the new event.
 *
 * @returns object.queueEvent -- queue an event for dispatching with an id, providing the event, a target on which the event can be
 * dispatched, and a delay in milliseconds during which dispatching of the event can be aborted and the event can be overwritten
 *
 * @returns object.getQueuedEvent -- retrieve an event that is queued for dispatching. Also returns an AbortController, that can
 * optionally be used to manually abort the dispatch
 *
 * @returns object.abortAndClearAll -- aborts dispatching of every event, and clears the queue. There is no need to do this,
 * because this hook cleans up after itself. It is retured for edge cases.
 * */
export const useEventDispatchQueue = <TEventDetail>(): EventDispatchQueue<TEventDetail> => {
    const buffer: EventDispatchQueueBuffer<TEventDetail> = {}
    const getQueuedEvent = (id: string): EventDispatchQueueBufferEntry<TEventDetail> | undefined => {
        return buffer[id]
    }
    const queueEvent = (
        id: string,
        event: CustomEvent<TEventDetail>,
        target: HTMLElement | Document,
        delayInMilliseconds: number,
    ) => {
        abortAndClearItemFromQueue(id, buffer)
        buffer[id] = { event, controller: new AbortController() }
        const signal = buffer[id].controller.signal
        const dispatch = () => {
            if (signal.aborted) {
                return
            }
            target.dispatchEvent(buffer[id].event)
            abortAndClearItemFromQueue(id, buffer)
        }
        if (!delayInMilliseconds) {
            dispatch() // dispatch within current event loop
            return
        }
        setTimeout(dispatch, delayInMilliseconds)
    }

    const abortAndClearAll = () => abortAllEventDispatchesAndClearQueue(buffer)

    useEffect(() => {
        return () => {
            abortAllEventDispatchesAndClearQueue(buffer)
        }
    }, [])
    return { queueEvent, getQueuedEvent, abortAndClearAll }
}
